/**
 * InputService - Mouse and keyboard input handling
 */

import { webRTCService } from './WebRTCService';
import { eventBus } from './EventBus';
import { clipboardQueryService } from './ClipboardQueryService';
import type { MouseInputMessage, KeyboardInputMessage, ClipboardMessage, InputState } from '../types';
import { INPUT_THROTTLE_MS, MOUSE_BUTTON_MAP, MOUSE_EVENT, KEY_EVENT } from '../constants';

class InputService {
  private videoElement: HTMLVideoElement | null = null;
  private videoContainer: HTMLElement | null = null;
  private enabled: InputState = { mouse: true, keyboard: true };
  private isActive = false;
  private lastMouseTime = 0;
  private pendingMouseMove: MouseInputMessage | null = null;
  private throttleTimer: number | null = null;
  private isMouseOverVideo = false;
  private clipboardEnabled = true;
  private pendingPasteResolve: ((text: string) => void) | null = null; // For paste event callback
  private waitingForPasteEvent = false; // Flag to capture next paste event for remote
  private lastSyncedClipboard = ''; // Track last synced content to avoid duplicates
  private lastSyncToastTime = 0; // Debounce toast (ms)
  // 注：曾有 remoteHasClipboardFiles 守卫（远端剪贴板有文件时跳过本地文本回发，
  // 防止覆盖远端文件剪贴板），两版"猜用户意图"实现均出 bug（2026-06-11/12）。
  // 已根治于 host 端：WriteClipboardText 写文本时备份并放回 CF_HDROP，
  // 文本与文件在远端剪贴板共存，粘贴目标应用自取格式，前端无须任何猜测。

  // 鼠标专用通道（低延迟）
  private mouseChannel: RTCDataChannel | null = null;

  // 通道断开时记录，恢复后自动重激活
  private deactivatedByChannelClose = false;

  // 文件管理器打开前的 isActive 状态（关闭后恢复）
  private wasActiveBeforeFileManager = false;

  // 任意遮罩弹窗（全局搜索 / 搜索引擎初始化等）打开时为 true，
  // 期间所有键鼠 handler 在最前面直接 return，避免输入泄漏到远程。
  private modalOpen = false;

  // DataChannel 就绪状态（用于检测重连后通道未就绪）
  // 默认为 true，因为事件可能在 initialize 之前就已经触发
  private dataChannelReady = true;

  // 游戏鼠标模式（相对位移）
  private relativeMouseMode = false;
  private pointerLockRequested = false;
  // 累积的相对位移（用于节流时不丢失移动）
  private accumulatedDeltaX = 0;
  private accumulatedDeltaY = 0;

  // 全屏键盘锁定状态（用于拦截系统快捷键）
  private keyboardLocked = false;

  // 长按 ESC 检测
  private escPressStartTime = 0;
  private escLongPressTimer: number | null = null;
  private readonly ESC_LONG_PRESS_DURATION = 500; // 500ms 长按触发

  // 游戏鼠标模式状态
  private gameMouseMode = false;

  // ===== 方案D: 平滑处理 - 大位移拆分队列 =====
  private smoothingQueue: { dx: number; dy: number }[] = [];
  private smoothingTimer: number | null = null;

  // ===== 方案E: 异常值过滤 - 历史位移记录 =====
  private recentDeltas: number[] = [];
  private readonly RECENT_DELTA_COUNT = 10;  // 记录最近10帧
  private readonly SPIKE_THRESHOLD = 20;      // 超过平均值20倍视为异常（放宽以支持快速移动）

  // ===== 相对移动模式节流 =====
  private relativeAccumX = 0;  // 累积的 X 位移
  private relativeAccumY = 0;  // 累积的 Y 位移
  private relativeThrottleTimer: number | null = null;
  private readonly RELATIVE_THROTTLE_MS = 4;  // 4ms ≈ 250Hz

  get inputEnabled(): InputState {
    return { ...this.enabled };
  }

  get isInputActive(): boolean {
    return this.isActive;
  }

  /**
   * Initialize with video element
   */
  initialize(videoElement: HTMLVideoElement, videoContainer?: HTMLElement): void {
    this.videoElement = videoElement;
    this.videoContainer = videoContainer || videoElement.parentElement;
    this.setupEventListeners();

    // Listen for clipboard sync from server
    eventBus.on('clipboard:sync', (text: string) => {
      this.handleClipboardFromServer(text);
    });

    // Listen for relative mouse mode toggle from settings
    eventBus.on('input:setRelativeMode', (enabled: boolean) => {
      this.setRelativeMouseMode(enabled);
    });

    // Listen for mouse channel from WebRTCService
    eventBus.on('webrtc:mouseChannel', (channel: RTCDataChannel) => {
      this.mouseChannel = channel;
    });

    // Listen for dataChannel ready state changes
    eventBus.on('webrtc:dataChannelReady', (ready: boolean) => {
      this.dataChannelReady = ready;

      // If channel closed while active, remember so we can auto-restore on reconnect
      if (!ready && this.isActive) {
        this.isActive = false;
        this.deactivatedByChannelClose = true;
        eventBus.emit('ui:toast', { message: '控制通道断开', type: 'warning' });
      }

      // Auto re-activate on reconnect if we were deactivated by channel close
      if (ready && this.deactivatedByChannelClose) {
        this.isActive = true;
        this.deactivatedByChannelClose = false;
      }
    });

    // Listen for keyboard lock state changes (fullscreen system key capture)
    eventBus.on('input:keyboardLocked', (locked: boolean) => {
      this.keyboardLocked = locked;
    });

    // Setup pointer lock change listener
    this.setupPointerLockListener();

    // Setup window focus/blur listeners to release modifier keys
    this.setupFocusListeners();

    // Force English input mode (disable IME)
    if (this.videoContainer) {
      this.videoContainer.setAttribute('lang', 'en');
      // Create a hidden input to capture focus and force English
      const focusTrap = document.createElement('input');
      focusTrap.type = 'text';
      focusTrap.inputMode = 'none';
      focusTrap.autocomplete = 'off';
      focusTrap.style.cssText = 'position:absolute;opacity:0;pointer-events:none;width:0;height:0;';
      focusTrap.id = 'input-focus-trap';
      this.videoContainer.appendChild(focusTrap);
    }
  }

  /**
   * Setup window focus/blur listeners to release modifier keys when browser loses focus
   * This prevents keys from getting "stuck" when user Alt+Tabs away
   */
  private setupFocusListeners(): void {
    // Release all modifier keys when window loses focus
    window.addEventListener('blur', this.handleWindowBlur);

    // Also release when window regains focus (safety measure)
    window.addEventListener('focus', this.handleWindowFocus);

    // Handle visibility change (tab switch, minimize)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Listen for video container focus changes
    if (this.videoContainer) {
      this.videoContainer.addEventListener('focusin', this.handleVideoFocusIn);
      this.videoContainer.addEventListener('focusout', this.handleVideoFocusOut);
      // Also use mouseenter/mouseleave as backup for focus detection
      this.videoContainer.addEventListener('mouseleave', this.handleVideoMouseLeave);
    }
  }

  /**
   * Handle video container focus in
   */
  private handleVideoFocusIn = (): void => {
    this.releaseAllModifierKeys();
  };

  /**
   * Handle video container focus out
   */
  private handleVideoFocusOut = (): void => {
    this.releaseAllModifierKeys();
  };

  /**
   * Handle mouse leaving video area - release modifiers as safety
   */
  private handleVideoMouseLeave = (): void => {
    // Only release if we have tracked system keys (avoid spamming keyup)
    if (this.fullscreenSystemKeys.size > 0) {
      this.releaseAllModifierKeys();
    }
  };

  /**
   * Handle window blur - release all modifier keys
   */
  private handleWindowBlur = (): void => {
    this.releaseAllModifierKeys();
  };

  /**
   * Handle window focus - release all modifier keys as safety measure
   */
  private handleWindowFocus = (): void => {
    this.releaseAllModifierKeys();
  };

  /**
   * Handle visibility change (tab switch)
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.releaseAllModifierKeys();
    }
  };

  /**
   * Release all modifier keys (Ctrl, Alt, Shift, Win) to remote
   */
  private releaseAllModifierKeys(): void {
    // Virtual key codes
    const VK_SHIFT = 0x10;
    const VK_CTRL = 0x11;
    const VK_ALT = 0x12;
    const VK_LWIN = 0x5B;
    const VK_RWIN = 0x5C;

    // Release all common modifier keys
    const modifiers = [
      { vk: VK_CTRL, extended: false },   // Ctrl
      { vk: VK_ALT, extended: false },    // Alt
      { vk: VK_SHIFT, extended: false },  // Shift
      { vk: VK_LWIN, extended: true },    // Left Win
      { vk: VK_RWIN, extended: true },    // Right Win
    ];

    for (const mod of modifiers) {
      this.sendInput({
        type: 'keyboard',
        vkCode: mod.vk,
        scanCode: 0,
        keyEvent: KEY_EVENT.UP,
        extended: mod.extended,
      });
    }

    // Clear any tracked system keys
    this.fullscreenSystemKeys.clear();

    // Clear suppressed keys
    this.suppressedKeys.clear();
  }

  // Debounce for pointer lock toast
  private lastPointerLockToastTime = 0;

  /**
   * Setup pointer lock change listener for relative mouse mode
   */
  private setupPointerLockListener(): void {
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement !== this.videoElement && this.relativeMouseMode && !this.modalOpen) {
        // Pointer lock lost - debounce toast (show only if >3s since last)
        // 弹窗打开导致的主动解锁不提示（!this.modalOpen）
        const now = Date.now();
        if (now - this.lastPointerLockToastTime > 3000) {
          this.lastPointerLockToastTime = now;
          eventBus.emit('ui:toast', { message: '鼠标已解锁，点击画面重新锁定', type: 'info' });
        }
      }
    });

    document.addEventListener('pointerlockerror', () => {
      console.error('[Input] Pointer lock error');
      //eventBus.emit('ui:toast', { message: '无法锁定鼠标，请重试', type: 'error' });
      this.pointerLockRequested = false;
    });
  }

  /**
   * Set relative mouse mode (game mode)
   */
  setRelativeMouseMode(enabled: boolean): void {
    this.relativeMouseMode = enabled;

    if (enabled) {
      // Try to request pointer lock immediately (may fail without user gesture)
      if (this.videoElement && !document.pointerLockElement) {
        this.videoElement.requestPointerLock().catch(() => {
          // Failed - will request on next click
        });
      }
    } else {
      // Exit pointer lock if currently locked
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
      this.pointerLockRequested = false;
    }
  }

  /**
   * Send cursor_config to Host (hide/show system cursor)
   */
  sendCursorConfig(hideSystemCursor: boolean): void {
    const msg = JSON.stringify({ type: 'cursor_config', hideSystemCursor });
    webRTCService.sendData(msg);
  }

  /**
   * Set game mouse mode
   */
  setGameMouseMode(enabled: boolean): void {
    this.gameMouseMode = enabled;
  }

  /**
   * Enable/disable input types
   */
  setEnabled(state: Partial<InputState>): void {
    this.enabled = { ...this.enabled, ...state };
    eventBus.emit('input:enabled', this.enabled);
  }

  /**
   * Enable/disable clipboard sync
   */
  setClipboardEnabled(enabled: boolean): void {
    this.clipboardEnabled = enabled;

    // Notify server about clipboard sync state (only if connected)
    if (webRTCService.isConnected) {
      const message: ClipboardMessage = {
        type: 'clipboard',
        action: enabled ? 'enable' : 'disable',
      };
      webRTCService.sendData(JSON.stringify(message));
    }
  }

  /**
   * Handle clipboard data received from server (after Ctrl+C on remote)
   */
  handleClipboardFromServer(base64Text: string): void {
    if (!this.clipboardEnabled) return;

    // Empty text means remote copied non-text content (image, file, etc.)
    if (!base64Text) {
      // Skip if last synced was also empty (avoid repeated "non-text" toasts)
      if (this.lastSyncedClipboard === '') return;
      this.lastSyncedClipboard = '';
      // Don't clear local clipboard or show toast for non-text
      return;
    }

    // Base64 decode
    let text: string;
    try {
      text = decodeURIComponent(escape(atob(base64Text)));
    } catch {
      console.error('[Input] Failed to decode base64 clipboard');
      return;
    }

    // Skip if same content as last sync
    if (text === this.lastSyncedClipboard) {
      return;
    }
    this.lastSyncedClipboard = text;


    // Write to local clipboard
    this.writeClipboard(text).then((success) => {
      if (success) {
        // Debounce toast - only show if more than 2 seconds since last toast
        const now = Date.now();
        if (now - this.lastSyncToastTime > 2000) {
          this.lastSyncToastTime = now;
          eventBus.emit('ui:toast', { message: '剪贴板已同步', type: 'success' });
        }
      } else {
        console.error('[Input] Failed to write clipboard');
        eventBus.emit('ui:toast', { message: '剪贴板写入失败', type: 'error' });
      }
    });
  }

  /**
   * Write to clipboard with fallback
   */
  private async writeClipboard(text: string): Promise<boolean> {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to fallback
      }
    }

    // Fallback: use hidden textarea and copy
    const textarea = document.createElement('textarea');
    textarea.style.cssText = 'position:fixed;left:-9999px;top:0;';
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  }

  /**
   * Send local clipboard to server (with chunking for large text)
   */
  private async sendClipboardToServer(): Promise<void> {
    if (!this.clipboardEnabled) {
      return;
    }

    try {
      let text = '';

      // Try modern Clipboard API first (requires HTTPS)
      if (navigator.clipboard && navigator.clipboard.readText) {
        text = await navigator.clipboard.readText();
      } else {
        // Fallback: use hidden textarea and paste event
        text = await this.readClipboardFallback();
      }

      if (text) {
        // Base64 encode to handle special characters
        const base64Text = btoa(unescape(encodeURIComponent(text)));

        // Chunk size: 48KB (safe for DataChannel, leaves room for JSON overhead)
        const CHUNK_SIZE = 48 * 1024;

        if (base64Text.length <= CHUNK_SIZE) {
          // Small text: send directly
          const message: ClipboardMessage = {
            type: 'clipboard',
            action: 'copy',
            text: base64Text,
          };
          webRTCService.sendData(JSON.stringify(message));
        } else {
          // Large text: send in chunks
          const totalChunks = Math.ceil(base64Text.length / CHUNK_SIZE);

          for (let i = 0; i < totalChunks; i++) {
            const chunk = base64Text.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const message = {
              type: 'clipboard',
              action: 'copy_chunk',
              chunk: i,
              total: totalChunks,
              text: chunk,
            };
            webRTCService.sendData(JSON.stringify(message));
          }
        }
      }
    } catch (err) {
      console.error('[Input] Failed to read clipboard:', err);
      eventBus.emit('ui:toast', { message: '无法读取剪贴板', type: 'warning' });
    }
  }

  /**
   * Fallback clipboard read using paste event (for HTTP)
   * Returns a promise that resolves when user triggers paste
   */
  private readClipboardFallback(): Promise<string> {
    return new Promise((resolve) => {
      // Set the resolve callback - will be called by handlePaste
      this.pendingPasteResolve = resolve;

      // Create and focus a hidden textarea to receive the paste
      const textarea = document.createElement('textarea');
      textarea.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0.01;';
      textarea.id = 'clipboard-capture';
      document.body.appendChild(textarea);
      textarea.focus();

      // Timeout after 500ms if no paste event
      setTimeout(() => {
        if (this.pendingPasteResolve) {
          // Check if textarea has content (paste might have happened)
          const text = textarea.value;
          if (text) {
            this.pendingPasteResolve(text);
          } else {
            this.pendingPasteResolve('');
          }
          this.pendingPasteResolve = null;
        }
        const el = document.getElementById('clipboard-capture');
        if (el) document.body.removeChild(el);
      }, 500);
    });
  }

  /**
   * Request clipboard from server (after Ctrl+C on remote)
   */
  private requestClipboardFromServer(): void {
    if (!this.clipboardEnabled) {
      return;
    }

    const message: ClipboardMessage = {
      type: 'clipboard',
      action: 'paste',
    };
    webRTCService.sendData(JSON.stringify(message));
  }

  /**
   * Activate input control (user clicked on video)
   */
  activate(): void {
    if (!this.enabled.mouse && !this.enabled.keyboard) {
      eventBus.emit('ui:toast', { message: '输入控制已禁用', type: 'warning' });
      return;
    }

    if (!webRTCService.isConnected) {
      return; // Silently ignore if not connected
    }

    const channel = webRTCService.inputChannel;
    const mouseChannel = webRTCService.mouseInputChannel;

    if (!channel || channel.readyState !== 'open') {
      eventBus.emit('ui:toast', { message: '数据通道未就绪', type: 'warning' });
      return;
    }

    this.isActive = true;
    eventBus.emit('ui:toast', { message: '已进入控制模式 (ESC 退出)', type: 'success' });
  }

  /**
   * Deactivate input control (ESC pressed)
   */
  deactivate(): void {
    this.isActive = false;
  }

  /**
   * File manager open/close toggle — auto deactivate control mode while FM is open,
   * then restore previous isActive on close. Does NOT show toast.
   */
  setFileManagerOpen(open: boolean): void {
    if (open) {
      this.wasActiveBeforeFileManager = this.isActive;
      this.isActive = false;
    } else {
      if (this.wasActiveBeforeFileManager && webRTCService.isConnected) {
        this.isActive = true;
      }
      this.wasActiveBeforeFileManager = false;
    }
  }

  /**
   * Modal open/close toggle — while any overlay modal (global file search,
   * search-engine init dialog, etc.) is open, swallow ALL keyboard/mouse input
   * at the very top of every handler so nothing leaks to the remote (e.g. typing
   * in the search box must not be forwarded). Does NOT touch isActive, so the
   * previous control-mode state is preserved once the modal closes.
   */
  setModalOpen(open: boolean): void {
    this.modalOpen = open;
    // 弹窗打开时释放鼠标锁：Pointer Lock 激活期间浏览器会忽略 input.focus()，
    // 导致搜索框拿不到键盘焦点。释放后由于 modalOpen gate 已挡住所有鼠标 handler，不会被立刻重新上锁。
    if (open && document.pointerLockElement) {
      document.exitPointerLock();
    }
  }

  /**
   * Check if mouse is over video area (not UI elements)
   */
  private isOverVideoArea(e: MouseEvent): boolean {
    if (!this.videoElement) return false;

    // Check if target is a UI element (not video container)
    const target = e.target as HTMLElement;

    // If clicking on UI elements (buttons, panels, status orb, etc.), return false
    if (target.closest('.glass, .glass-high, .glass-light, button, [role="button"], input, select, [data-ui-element]')) {
      return false;
    }

    // Check if within video bounds
    const coords = this.calculateCoordinates(e);
    return coords !== null;
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.videoElement) return;

    // Track mouse enter/leave video area
    window.addEventListener('mousemove', this.handleMouseEnterLeave);

    // Use window events since video has pointer-events: none
    // Mouse events
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('contextmenu', (e) => {
      if (this.isActive && this.isMouseOverVideo) e.preventDefault();
    });

    // Keyboard events - only send when mouse is over video
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    // Block IME composition events
    window.addEventListener('compositionstart', this.handleCompositionStart);
    window.addEventListener('compositionupdate', this.handleCompositionUpdate);

    // Paste event - to capture clipboard content on HTTP
    window.addEventListener('paste', this.handlePaste);
  }

  /**
   * Handle paste event to get clipboard content
   */
  private handlePaste = (e: ClipboardEvent): void => {
    // For fallback clipboard read
    if (this.pendingPasteResolve && e.clipboardData) {
      const text = e.clipboardData.getData('text/plain');
      this.pendingPasteResolve(text);
      this.pendingPasteResolve = null;
      e.preventDefault();
      return;
    }

    // For Ctrl+V to remote - capture and send to server
    if (this.waitingForPasteEvent && e.clipboardData) {
      this.waitingForPasteEvent = false;

      // Check if clipboard contains files - if so, let App.tsx handle it
      // App.tsx will send manifest and backend will send Ctrl+V
      const items = e.clipboardData.items;
      let hasFiles = false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          hasFiles = true;
          break;
        }
      }

      if (hasFiles) {
        // Don't prevent default - let App.tsx handlePaste process files
        // Don't send Ctrl+V - backend will send it after setting virtual clipboard
        return;
      }

      const text = e.clipboardData.getData('text/plain');
      e.preventDefault();

      // 无条件回发本地文本。远端文件剪贴板的保护已下沉到 host 端：
      // WriteClipboardText 会备份并放回 CF_HDROP，文本与文件共存，
      // 远端粘贴目标应用自取格式（记事本取文本 / Explorer 取文件），前端无须猜意图。
      if (this.clipboardEnabled && text) {
        const base64Text = btoa(unescape(encodeURIComponent(text)));
        const message: ClipboardMessage = {
          type: 'clipboard',
          action: 'copy',
          text: base64Text,
        };
        webRTCService.sendData(JSON.stringify(message));
      }

      // Send Ctrl+V to remote (for text paste only)
      this.sendCtrlV();
    }
  };

  /**
   * Send Ctrl+V key combination to remote
   */
  private sendCtrlV(): void {
    this.sendInput({ type: 'keyboard', vkCode: 0x11, scanCode: 0, keyEvent: KEY_EVENT.DOWN, extended: false });
    this.sendInput({ type: 'keyboard', vkCode: 0x56, scanCode: 0, keyEvent: KEY_EVENT.DOWN, extended: false });
    setTimeout(() => {
      this.sendInput({ type: 'keyboard', vkCode: 0x56, scanCode: 0, keyEvent: KEY_EVENT.UP, extended: false });
      this.sendInput({ type: 'keyboard', vkCode: 0x11, scanCode: 0, keyEvent: KEY_EVENT.UP, extended: false });
    }, 20);
  }

  /**
   * Track mouse entering/leaving video area
   */
  private handleMouseEnterLeave = (e: MouseEvent): void => {
    this.isMouseOverVideo = this.isOverVideoArea(e);
  };

  /**
   * Calculate normalized coordinates (0-65535)
   */
  private calculateCoordinates(e: MouseEvent): { x: number; y: number } | null {
    if (!this.videoElement) return null;

    const video = this.videoElement;
    const rect = video.getBoundingClientRect();

    // Get actual video dimensions
    const videoWidth = video.videoWidth || 1920;
    const videoHeight = video.videoHeight || 1080;

    // Calculate aspect ratios
    const videoAspect = videoWidth / videoHeight;
    const rectAspect = rect.width / rect.height;

    let actualWidth: number;
    let actualHeight: number;
    let offsetX = 0;
    let offsetY = 0;

    if (rectAspect > videoAspect) {
      // Pillarbox (video is narrower)
      actualHeight = rect.height;
      actualWidth = actualHeight * videoAspect;
      offsetX = (rect.width - actualWidth) / 2;
    } else {
      // Letterbox (video is wider)
      actualWidth = rect.width;
      actualHeight = actualWidth / videoAspect;
      offsetY = (rect.height - actualHeight) / 2;
    }

    // Calculate relative position
    const relX = e.clientX - rect.left - offsetX;
    const relY = e.clientY - rect.top - offsetY;

    // Check if within video area
    if (relX < 0 || relX > actualWidth || relY < 0 || relY > actualHeight) {
      return null;
    }

    // Normalize to 0-65535
    const x = Math.round((relX / actualWidth) * 65535);
    const y = Math.round((relY / actualHeight) * 65535);

    return {
      x: Math.max(0, Math.min(65535, x)),
      y: Math.max(0, Math.min(65535, y)),
    };
  }

  /**
   * ===== 方案D: 平滑发送定时器 =====
   * 将队列中的位移分帧发送，每帧间隔 8ms (~120Hz)
   */
  private startSmoothingTimer(): void {
    if (this.smoothingTimer) return;  // 已经在运行

    this.smoothingTimer = window.setInterval(() => {
      const item = this.smoothingQueue.shift();
      if (item) {
        this.sendInput({
          type: 'mouse',
          x: 0,
          y: 0,
          eventType: MOUSE_EVENT.MOVE,
          relative: true,
          deltaX: item.dx,
          deltaY: item.dy,
        });
      } else {
        // 队列空了，停止定时器
        if (this.smoothingTimer) {
          clearInterval(this.smoothingTimer);
          this.smoothingTimer = null;
        }
      }
    }, 8);  // 8ms ≈ 120Hz
  }

  /**
   * Send input message
   * Mouse messages use dedicated mouseChannel for lower latency
   */
  private sendInput(message: MouseInputMessage | KeyboardInputMessage): void {
    if (webRTCService.idleBlocking) return;
    const json = JSON.stringify(message);

    // Use dedicated mouse channel for mouse messages (lower latency)
    if (message.type === 'mouse' && this.mouseChannel?.readyState === 'open') {
      this.mouseChannel.send(json);
    } else {
      const sent = webRTCService.sendData(json);
      // Log if send failed (channel not open)
      if (!sent && message.type === 'keyboard') {
        console.warn('[Input] sendInput failed: dataChannel not open, type:', message.type);
      }
    }
  }

  /**
   * Handle mouse move with throttling
   */
  private handleMouseMove = (e: MouseEvent): void => {
    if (this.modalOpen) return;
    if (!this.isActive || !this.enabled.mouse) return;

    // 相对位移模式：只要 Pointer Lock 激活就使用相对移动
    // 不需要检查 relativeMouseMode，因为规则是"光标隐藏就用相对移动"
    if (document.pointerLockElement === this.videoElement) {
      const rawDeltaX = e.movementX;
      const rawDeltaY = e.movementY;

      // 只有实际移动时才累积
      if (rawDeltaX === 0 && rawDeltaY === 0) return;

      // ===== 累积位移，节流发送 =====
      this.relativeAccumX += rawDeltaX;
      this.relativeAccumY += rawDeltaY;

      // 如果已有定时器在运行，等待它发送
      if (this.relativeThrottleTimer) return;

      // 启动节流定时器
      this.relativeThrottleTimer = window.setTimeout(() => {
        this.relativeThrottleTimer = null;

        // 获取累积的位移并重置
        const accumX = this.relativeAccumX;
        const accumY = this.relativeAccumY;
        this.relativeAccumX = 0;
        this.relativeAccumY = 0;

        // 如果没有累积位移，不发送
        if (accumX === 0 && accumY === 0) return;

        // 直接发送累积的位移（不限制大小，Go 端使用 int16）
        this.sendInput({
          type: 'mouse',
          x: 0,
          y: 0,
          eventType: MOUSE_EVENT.MOVE,
          relative: true,
          deltaX: Math.round(accumX),
          deltaY: Math.round(accumY),
        });
      }, this.RELATIVE_THROTTLE_MS);
      return;
    }

    // 绝对位移模式（原有逻辑）
    if (!this.isMouseOverVideo) return;

    const coords = this.calculateCoordinates(e);
    if (!coords) return;

    const message: MouseInputMessage = {
      type: 'mouse',
      x: coords.x,
      y: coords.y,
      eventType: MOUSE_EVENT.MOVE,
    };

    // Throttle mouse move events
    const now = performance.now();
    if (now - this.lastMouseTime < INPUT_THROTTLE_MS) {
      this.pendingMouseMove = message;
      if (!this.throttleTimer) {
        this.throttleTimer = window.setTimeout(() => {
          if (this.pendingMouseMove) {
            this.sendInput(this.pendingMouseMove);
            this.pendingMouseMove = null;
          }
          this.throttleTimer = null;
          this.lastMouseTime = performance.now();
        }, INPUT_THROTTLE_MS - (now - this.lastMouseTime));
      }
      return;
    }

    this.lastMouseTime = now;
    this.sendInput(message);
  };

  /**
   * Handle mouse down
   */
  private handleMouseDown = (e: MouseEvent): void => {
    if (this.modalOpen) return;
    if (!this.enabled.mouse) {
      return;
    }

    // Don't process if clicking on UI elements
    if (!this.isOverVideoArea(e)) {
      return;
    }

    // Auto-activate on first click in video area
    if (!this.isActive) {
      this.activate();
      // In relative mode, request pointer lock on activation
      if (this.relativeMouseMode && this.videoElement && !document.pointerLockElement) {
        this.videoElement.requestPointerLock();
      }
      return; // Don't send this click, just activate
    }

    // In relative mode, request pointer lock if not already locked
    if (this.relativeMouseMode && this.videoElement && !document.pointerLockElement) {
      this.videoElement.requestPointerLock();
      return; // Don't send this click, just lock
    }

    e.preventDefault();

    // 相对位移模式下，坐标不重要
    if (document.pointerLockElement) {
      this.sendInput({
        type: 'mouse',
        x: 0,
        y: 0,
        eventType: MOUSE_EVENT.DOWN,
        button: MOUSE_BUTTON_MAP[e.button] || 1,
        relative: true,
      });
      return;
    }

    const coords = this.calculateCoordinates(e);
    if (!coords) {
      return;
    }

    this.sendInput({
      type: 'mouse',
      x: coords.x,
      y: coords.y,
      eventType: MOUSE_EVENT.DOWN,
      button: MOUSE_BUTTON_MAP[e.button] || 1,
    });
  };

  /**
   * Handle mouse up
   */
  private handleMouseUp = (e: MouseEvent): void => {
    if (this.modalOpen) return;
    if (!this.isActive || !this.enabled.mouse) return;

    // 相对位移模式
    if (document.pointerLockElement) {
      e.preventDefault();
      this.sendInput({
        type: 'mouse',
        x: 0,
        y: 0,
        eventType: MOUSE_EVENT.UP,
        button: MOUSE_BUTTON_MAP[e.button] || 1,
        relative: true,
      });
      return;
    }

    if (!this.isMouseOverVideo) return;

    const coords = this.calculateCoordinates(e);
    if (!coords) return;

    e.preventDefault();

    this.sendInput({
      type: 'mouse',
      x: coords.x,
      y: coords.y,
      eventType: MOUSE_EVENT.UP,
      button: MOUSE_BUTTON_MAP[e.button] || 1,
    });
  };

  /**
   * Handle mouse wheel
   */
  private handleWheel = (e: WheelEvent): void => {
    if (this.modalOpen) return;
    if (!this.isActive || !this.enabled.mouse) return;

    // Normalize wheel delta
    const wheelDelta = e.deltaY < 0 ? 120 : -120;

    // 相对位移模式
    if (document.pointerLockElement) {
      e.preventDefault();
      this.sendInput({
        type: 'mouse',
        x: 0,
        y: 0,
        eventType: MOUSE_EVENT.WHEEL,
        wheelDelta,
        relative: true,
      });
      return;
    }

    if (!this.isMouseOverVideo) return;

    const coords = this.calculateCoordinates(e);
    if (!coords) return;

    e.preventDefault();

    this.sendInput({
      type: 'mouse',
      x: coords.x,
      y: coords.y,
      eventType: MOUSE_EVENT.WHEEL,
      wheelDelta,
    });
  };

  /**
   * Handle system keys in fullscreen mode (when keyboard is locked)
   * Returns true if the key was handled as a system key
   */
  private handleSystemKeyInFullscreen(e: KeyboardEvent): boolean {
    // Virtual key codes for Windows
    const VK_TAB = 0x09;
    const VK_ESCAPE = 0x1B;
    const VK_ALT = 0x12;
    const VK_LWIN = 0x5B;
    const VK_RWIN = 0x5C;
    const VK_F4 = 0x73;

    // Check for modifier keys themselves (Alt, Win)
    const isAltKey = e.code === 'AltLeft' || e.code === 'AltRight';
    const isWinKey = e.code === 'MetaLeft' || e.code === 'MetaRight';

    // Check for system key combinations
    const isAltTab = e.altKey && e.code === 'Tab';
    const isAltF4 = e.altKey && e.code === 'F4';
    const isWinCombo = e.metaKey && !isWinKey;  // Windows key + something (but not Win key itself)
    const isEscape = e.code === 'Escape';

    // Handle Alt key itself - MUST track it for proper keyup
    if (isAltKey) {
      e.preventDefault();
      e.stopPropagation();


      this.sendInput({
        type: 'keyboard',
        vkCode: VK_ALT,
        scanCode: 0,
        keyEvent: KEY_EVENT.DOWN,
        extended: e.code === 'AltRight',
      });

      this.fullscreenSystemKeys.add(e.code);
      return true;
    }

    // Handle Windows key itself
    if (isWinKey) {
      e.preventDefault();
      e.stopPropagation();

      const vkCode = e.code === 'MetaLeft' ? VK_LWIN : VK_RWIN;

      this.sendInput({
        type: 'keyboard',
        vkCode: vkCode,
        scanCode: 0,
        keyEvent: KEY_EVENT.DOWN,
        extended: true,
      });

      this.fullscreenSystemKeys.add(e.code);
      return true;
    }

    // Handle Escape - release all modifiers first, then send Escape
    if (isEscape) {
      e.preventDefault();
      e.stopPropagation();

      this.releaseAllModifierKeys();

      // Send Escape key
      this.sendInput({
        type: 'keyboard',
        vkCode: VK_ESCAPE,
        scanCode: 0,
        keyEvent: KEY_EVENT.DOWN,
        extended: false,
      });

      this.fullscreenSystemKeys.add(e.code);
      return true;
    }

    // Handle system key combinations (Tab, F4, etc. when modifier is held)
    if (isAltTab || isAltF4 || isWinCombo) {
      e.preventDefault();
      e.stopPropagation();

      // Determine the VK code
      let vkCode = e.keyCode;

      // Map code to VK if needed
      if (e.code === 'Tab') vkCode = VK_TAB;
      else if (e.code === 'F4') vkCode = VK_F4;


      this.sendInput({
        type: 'keyboard',
        vkCode: vkCode,
        scanCode: 0,
        keyEvent: KEY_EVENT.DOWN,
        extended: false,
      });

      // Track for keyup
      this.fullscreenSystemKeys.add(e.code);
      return true;
    }

    return false;
  }

  // Track system keys pressed in fullscreen for proper keyup handling
  private fullscreenSystemKeys: Set<string> = new Set();

  /**
   * Handle key down
   */
  private handleKeyDown = (e: KeyboardEvent): void => {
    // A modal overlay (search box, init dialog) is open: never forward any key
    // to the remote, including fullscreen system keys / Alt+F1.
    if (this.modalOpen) return;

    // In fullscreen mode, handle system keys specially (regardless of keyboard lock state)
    // This ensures ESC and other system keys are sent to remote even if Keyboard Lock API failed
    if (document.fullscreenElement) {
      const isSystemKey = this.handleSystemKeyInFullscreen(e);
      if (isSystemKey) return;
    }

    // Alt+F1: toggle game mouse mode
    if (e.code === 'F1' && e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      const newMode = !this.gameMouseMode;
      eventBus.emit('input:gameMouseMode', newMode);
      return;
    }

    // Alt+F: open global file search (handled by StatusBar)
    if (e.code === 'KeyF' && e.altKey && !e.ctrlKey && !e.shiftKey && !e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      eventBus.emit('file_search:openSearchHotkey', undefined as never);
      return;
    }

    // Keyboard gating: once in control mode, intercept ALL keys regardless of mouse position.
    // This fixes the intermittent failure where Ctrl+C / Ctrl+V / Ctrl+S etc. were dropped when
    // the mouse happened to hover over a UI element (glass panel, button, toast...).
    if (!this.isActive || !this.enabled.keyboard) return;

    // Let local input elements (settings inputs, chat fields, etc.) handle keys normally.
    // Excludes the hidden 'input-focus-trap' created for IME suppression.
    {
      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable)) {
        if (activeEl.id !== 'input-focus-trap') return;
      }
    }

    // ESC key: release all modifier keys + long press detection for force show cursor
    if (e.code === 'Escape') {
      this.releaseAllModifierKeys();

      // 非游戏模式：长按 ESC 强制显示光标
      if (!this.gameMouseMode && !e.repeat && !this.escLongPressTimer) {
        this.escPressStartTime = Date.now();
        this.escLongPressTimer = window.setTimeout(() => {
          eventBus.emit('input:forceShowCursor', true);
          if (document.pointerLockElement) {
            document.exitPointerLock();
          }
          this.escLongPressTimer = null;
        }, this.ESC_LONG_PRESS_DURATION);
      }
      // Continue to send ESC key normally
    }

    // Handle clipboard shortcuts specially when clipboard sync is enabled
    if (this.clipboardEnabled && (e.ctrlKey || e.metaKey)) {
      const key = e.key.toLowerCase();

      // Ctrl+C: 转发 Ctrl+C 给 host，然后 220ms 后：
      //   1) 拉取剪贴板文本（同步到本地，保留原有能力）
      //   2) 拉模式查询剪贴板文件，命中则 emit filesAvailable 触发 Toast
      // 220ms 覆盖：input channel 与 data channel 时序差 + host 端 SendInput→剪贴板写入时间
      if (key === 'c') {
        e.preventDefault();
        this.suppressedKeys.add('ctrl+c');
        this.sendInput({ type: 'keyboard', vkCode: 0x11, scanCode: 0, keyEvent: KEY_EVENT.DOWN, extended: false });
        this.sendInput({ type: 'keyboard', vkCode: 0x43, scanCode: 0, keyEvent: KEY_EVENT.DOWN, extended: false });
        setTimeout(() => {
          this.sendInput({ type: 'keyboard', vkCode: 0x43, scanCode: 0, keyEvent: KEY_EVENT.UP, extended: false });
          this.sendInput({ type: 'keyboard', vkCode: 0x11, scanCode: 0, keyEvent: KEY_EVENT.UP, extended: false });
          this.suppressedKeys.delete('ctrl+c');
          setTimeout(async () => {
            // 文本同步（沿用 ClipboardHandler 路径）
            this.requestClipboardFromServer();
            // 拉模式文件查询
            try {
              const r = await clipboardQueryService.query('ctrl_c');
              if (r.hasFiles && r.files.length > 0) {
                eventBus.emit('clipboard:filesAvailable', {
                  files: r.files,
                  totalSize: r.totalSize,
                  fileCount: r.fileCount,
                });
              }
            } catch (err) {
              console.log('[Input] Ctrl+C query failed:', (err as Error).message);
            }
          }, 220);
        }, 20);
        return;
      }

      // Ctrl+V: Paste to remote - let paste event fire to capture clipboard
      if (key === 'v') {
        // DON'T prevent default - let paste event fire so we can capture clipboard
        // Mark as suppressed so keyup won't send duplicate events
        this.suppressedKeys.add('ctrl+v');
        this.waitingForPasteEvent = true;
        // Fallback: if no paste event within 100ms (empty clipboard), send Ctrl+V anyway
        setTimeout(() => {
          if (this.waitingForPasteEvent) {
            this.waitingForPasteEvent = false;
            this.sendCtrlV();
          }
          // Clear suppression after handling
          setTimeout(() => this.suppressedKeys.delete('ctrl+v'), 50);
        }, 100);
        return;
      }
    }

    // Block browser shortcuts when active
    if (e.ctrlKey || e.metaKey) {
      const key = e.key.toLowerCase();
      if (['s', 'n', 'o', 't', 'w', 'r', 'p'].includes(key)) {
        e.preventDefault();
      }
    }

    // Prevent default for most keys to avoid browser handling
    e.preventDefault();

    this.sendInput({
      type: 'keyboard',
      vkCode: e.keyCode,
      scanCode: 0, // Will be computed server-side
      keyEvent: KEY_EVENT.DOWN,
      extended: e.metaKey || e.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT,
    });
  };

  // Track keys that were handled specially in keydown (should skip keyup)
  private suppressedKeys: Set<string> = new Set();

  /**
   * Handle key up
   */
  private handleKeyUp = (e: KeyboardEvent): void => {
    if (this.modalOpen) return;
    // ESC release: cancel long press timer + 游戏模式下自动重锁 Pointer Lock
    if (e.code === 'Escape') {
      if (this.escLongPressTimer) {
        clearTimeout(this.escLongPressTimer);
        this.escLongPressTimer = null;
      }
      // 游戏模式下 ESC 短按后自动重锁（keyup 有用户手势上下文）
      if (this.gameMouseMode && this.videoElement && !document.pointerLockElement) {
        this.videoElement.requestPointerLock().catch(() => {
        });
      }
    }

    // Handle system keys in fullscreen mode (regardless of keyboard lock state)
    if (document.fullscreenElement && this.fullscreenSystemKeys.has(e.code)) {
      e.preventDefault();
      e.stopPropagation();

      // Map code to VK
      const VK_TAB = 0x09;
      const VK_ESCAPE = 0x1B;
      const VK_ALT = 0x12;
      const VK_LWIN = 0x5B;
      const VK_RWIN = 0x5C;
      const VK_F4 = 0x73;

      let vkCode = e.keyCode;
      let isExtended = false;

      if (e.code === 'AltLeft') {
        vkCode = VK_ALT;
        isExtended = false;
      } else if (e.code === 'AltRight') {
        vkCode = VK_ALT;
        isExtended = true;
      } else if (e.code === 'MetaLeft') {
        vkCode = VK_LWIN;
        isExtended = true;
      } else if (e.code === 'MetaRight') {
        vkCode = VK_RWIN;
        isExtended = true;
      } else if (e.code === 'Tab') {
        vkCode = VK_TAB;
      } else if (e.code === 'F4') {
        vkCode = VK_F4;
      } else if (e.code === 'Escape') {
        vkCode = VK_ESCAPE;
      }


      this.sendInput({
        type: 'keyboard',
        vkCode: vkCode,
        scanCode: 0,
        keyEvent: KEY_EVENT.UP,
        extended: isExtended,
      });

      this.fullscreenSystemKeys.delete(e.code);
      return;
    }

    // Keyboard gating: symmetric with handleKeyDown — do not require mouse over video.
    if (!this.isActive || !this.enabled.keyboard) return;

    // Let local input elements handle keys normally (symmetric with keydown).
    {
      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable)) {
        if (activeEl.id !== 'input-focus-trap') return;
      }
    }

    // Skip keyup for keys that were handled specially in keydown
    const keyId = `${e.ctrlKey || e.metaKey ? 'ctrl+' : ''}${e.key.toLowerCase()}`;
    if (this.suppressedKeys.has(keyId)) {
      this.suppressedKeys.delete(keyId);
      e.preventDefault();
      return;
    }

    // Also skip Ctrl/V keyup if we recently handled Ctrl+V specially
    if (this.suppressedKeys.has('ctrl+v')) {
      if (e.keyCode === 0x11 || e.keyCode === 0x56) { // Ctrl or V
        e.preventDefault();
        return;
      }
    }

    e.preventDefault();

    this.sendInput({
      type: 'keyboard',
      vkCode: e.keyCode,
      scanCode: 0,
      keyEvent: KEY_EVENT.UP,
      extended: e.metaKey || e.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT,
    });
  };

  /**
   * Block IME composition - prevents input method from activating
   */
  private handleCompositionStart = (e: CompositionEvent): void => {
    // 弹窗(搜索框等)打开时不抑制 IME，否则会 blur 掉搜索框导致中文输入时焦点消失。
    // 与 handleKeyDown / handleKeyUp 的 modalOpen 守卫保持一致。
    if (this.modalOpen) return;
    if (this.isActive && this.isMouseOverVideo && this.enabled.keyboard) {
      e.preventDefault();
      // Clear any IME input by blurring and refocusing
      const activeEl = document.activeElement as HTMLElement;
      if (activeEl && activeEl.blur) {
        activeEl.blur();
      }
    }
  };

  private handleCompositionUpdate = (e: CompositionEvent): void => {
    if (this.modalOpen) return;
    if (this.isActive && this.isMouseOverVideo && this.enabled.keyboard) {
      e.preventDefault();
    }
  };

  /**
   * Send special key combination
   */
  sendSpecialKey(combo: string): void {

    const keySequence: Array<{ vk: number; down: boolean }> = [];

    switch (combo) {
      case 'ctrl+alt+del':
        // Ctrl+Alt+Delete is a Windows Secure Attention Sequence (SAS)
        // SendInput cannot simulate it. Send a special message to trigger SAS API on backend.
        this.sendInput({
          type: 'special_key',
          key: 'ctrl+alt+del',
        });
        break;

      case 'unlock':
        // Unlock screen: click center then press Enter
        // This works for Windows lock screen
        this.sendInput({
          type: 'special_key',
          key: 'unlock',
        });
        break;

      case 'alt+tab':
        keySequence.push({ vk: 0x12, down: true });  // ALT down
        keySequence.push({ vk: 0x09, down: true });  // TAB down
        keySequence.push({ vk: 0x09, down: false }); // TAB up
        keySequence.push({ vk: 0x12, down: false }); // ALT up
        break;

      case 'win+d':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x44, down: true });  // D down
        keySequence.push({ vk: 0x44, down: false }); // D up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+l':
        // Win+L cannot be reliably triggered by simulated keystrokes (Windows
        // intercepts it at the system level). Send a special message so the host
        // calls LockWorkStation() directly.
        this.sendInput({
          type: 'special_key',
          key: 'win+l',
        });
        break;

      case 'ctrl+shift+esc':
        keySequence.push({ vk: 0x11, down: true });  // CTRL down
        keySequence.push({ vk: 0x10, down: true });  // SHIFT down
        keySequence.push({ vk: 0x1B, down: true });  // ESC down
        keySequence.push({ vk: 0x1B, down: false }); // ESC up
        keySequence.push({ vk: 0x10, down: false }); // SHIFT up
        keySequence.push({ vk: 0x11, down: false }); // CTRL up
        break;

      case 'win+tab':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x09, down: true });  // TAB down
        keySequence.push({ vk: 0x09, down: false }); // TAB up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'alt+f4':
        keySequence.push({ vk: 0x12, down: true });  // ALT down
        keySequence.push({ vk: 0x73, down: true });  // F4 down
        keySequence.push({ vk: 0x73, down: false }); // F4 up
        keySequence.push({ vk: 0x12, down: false }); // ALT up
        break;

      case 'f11':
        keySequence.push({ vk: 0x7A, down: true });  // F11 down
        keySequence.push({ vk: 0x7A, down: false }); // F11 up
        break;

      case 'win+e':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x45, down: true });  // E down
        keySequence.push({ vk: 0x45, down: false }); // E up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+r':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x52, down: true });  // R down
        keySequence.push({ vk: 0x52, down: false }); // R up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+s':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x53, down: true });  // S down
        keySequence.push({ vk: 0x53, down: false }); // S up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+i':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x49, down: true });  // I down
        keySequence.push({ vk: 0x49, down: false }); // I up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+v':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x56, down: true });  // V down
        keySequence.push({ vk: 0x56, down: false }); // V up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+p':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x50, down: true });  // P down
        keySequence.push({ vk: 0x50, down: false }); // P up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;

      case 'win+shift+s':
        keySequence.push({ vk: 0x5B, down: true });  // WIN down
        keySequence.push({ vk: 0x10, down: true });  // SHIFT down
        keySequence.push({ vk: 0x53, down: true });  // S down
        keySequence.push({ vk: 0x53, down: false }); // S up
        keySequence.push({ vk: 0x10, down: false }); // SHIFT up
        keySequence.push({ vk: 0x5B, down: false }); // WIN up
        break;
    }

    // Send key sequence with delays (50ms for reliable Windows processing)
    keySequence.forEach((key, index) => {
      setTimeout(() => {
        this.sendInput({
          type: 'keyboard',
          vkCode: key.vk,
          scanCode: 0,
          keyEvent: key.down ? KEY_EVENT.DOWN : KEY_EVENT.UP,
          extended: key.vk === 0x5B || key.vk === 0x5C, // WIN keys are extended
        });
      }, index * 50);
    });
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
    }

    // 清理平滑发送定时器
    if (this.smoothingTimer) {
      clearInterval(this.smoothingTimer);
      this.smoothingTimer = null;
    }
    this.smoothingQueue = [];

    if (this.videoElement) {
      this.videoElement.removeEventListener('mousemove', this.handleMouseMove);
      this.videoElement.removeEventListener('mousedown', this.handleMouseDown);
      this.videoElement.removeEventListener('mouseup', this.handleMouseUp);
      this.videoElement.removeEventListener('wheel', this.handleWheel);
    }

    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('compositionstart', this.handleCompositionStart);
    window.removeEventListener('compositionupdate', this.handleCompositionUpdate);

    // Clean up focus listeners
    window.removeEventListener('blur', this.handleWindowBlur);
    window.removeEventListener('focus', this.handleWindowFocus);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Clean up video container listeners
    if (this.videoContainer) {
      this.videoContainer.removeEventListener('focusin', this.handleVideoFocusIn);
      this.videoContainer.removeEventListener('focusout', this.handleVideoFocusOut);
      this.videoContainer.removeEventListener('mouseleave', this.handleVideoMouseLeave);
    }
  }
}

// Singleton instance
export const inputService = new InputService();
export default inputService;

// ===== 诊断用：把单例挂到 window.__dbg，便于现场 console 取状态 =====
// 用法：在 F12 console 跑 __dbg.dump()，输出完整状态快照
if (typeof window !== 'undefined') {
  const dbg = {
    inputService,
    webRTCService,
    dump() {
      const i = inputService as unknown as Record<string, unknown>;
      const w = webRTCService as unknown as Record<string, unknown>;
      const videoEl = i.videoElement as HTMLVideoElement | null;
      const mouseChan = (w.mouseInputChannel ?? w._mouseInputChannel) as RTCDataChannel | undefined;
      const inputChan = (w.inputChannel ?? w._inputChannel) as RTCDataChannel | undefined;
      const snapshot = {
        // === InputService 状态 ===
        isActive:           inputService.isInputActive,
        enabled:            inputService.inputEnabled,
        relativeMouseMode:  i.relativeMouseMode,
        gameMouseMode:      i.gameMouseMode,
        isMouseOverVideo:   i.isMouseOverVideo,
        clipboardEnabled:   i.clipboardEnabled,
        deactivatedByChannelClose: i.deactivatedByChannelClose,
        dataChannelReady:   i.dataChannelReady,
        // === Pointer Lock ===
        pointerLockElement:     !!document.pointerLockElement,
        pointerLockIsVideo:     document.pointerLockElement === videoEl,
        pointerLockTagName:     (document.pointerLockElement as HTMLElement | null)?.tagName,
        // === Video Element ===
        videoEl_inDom:          videoEl ? document.contains(videoEl) : null,
        videoEl_isDocVideo:     videoEl === document.querySelector('video'),
        videoEl_rect:           videoEl?.getBoundingClientRect()?.toJSON?.() ?? null,
        videoEl_videoWH:        [videoEl?.videoWidth, videoEl?.videoHeight],
        videoEl_paused:         videoEl?.paused,
        videoEl_readyState:     videoEl?.readyState,
        // === Mouse Channel ===
        mouseChan_state:        mouseChan?.readyState,
        mouseChan_bufAmount:    mouseChan?.bufferedAmount,
        mouseChan_label:        mouseChan?.label,
        mouseChan_ordered:      mouseChan?.ordered,
        // === Input Channel ===
        inputChan_state:        inputChan?.readyState,
        inputChan_bufAmount:    inputChan?.bufferedAmount,
        // === Idle ===
        idleBlocking:           w.idleBlocking ?? w._idleBlocking,
        idlePaused:             w.idlePaused,
        idleCountdown:          w.idleCountdown,
        // === Misc ===
        fullscreen:             !!document.fullscreenElement,
        visibility:             document.visibilityState,
        hasFocus:               document.hasFocus(),
      };
      console.table(snapshot);
      console.log('[__dbg.dump] 完整对象（可展开）:', snapshot);
      // 视频中心点的元素栈：用来排查全屏覆盖物
      const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
      const stack = document.elementsFromPoint(cx, cy);
      console.log('[__dbg.dump] elementsFromPoint(center) 栈（最上层在前）:', stack);
      return snapshot;
    },
  };
  (window as unknown as Record<string, unknown>).__dbg = dbg;
  console.log('[__dbg] 已挂载到 window.__dbg，使用 __dbg.dump() 输出状态快照');
}
